import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import SafeIcon from '../components/common/SafeIcon';
import { MENTORS } from '../data/mentors';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiTrash2, FiClock, FiX, FiFilter, FiWind, FiEdit, FiCheckSquare } = FiIcons;

const HERO_IMAGE = "https://cdn.midjourney.com/9ec9662d-e584-4897-a037-12e498939509/0_0.webp";
const STORAGE_KEY = 'mindgarden_grove_v1';

const COLUMNS = [
  { id: 'planted', title: 'Planted', color: '#6E766A', description: 'Seeds of ideas' },
  { id: 'growing', title: 'Growing', color: '#E2A93B', description: 'Nurturing in progress' },
  { id: 'blooming', title: 'Blooming', color: '#6DAEDB', description: 'Nearing completion' },
  { id: 'harvested', title: 'Harvested', color: '#9BB898', description: 'Goals achieved' },
];

const PRIORITY_LEVELS = {
  low: { label: 'Low', color: '#9BB898' },
  medium: { label: 'Medium', color: '#E2A93B' },
  high: { label: 'High', color: '#D14D4D' },
};

const SAMPLE_TASKS = [
  { id: 'seed-1', title: 'Draft Q3 marketing plan', description: 'Outline key initiatives and budget for the next quarter.', column: 'planted', priority: 'high', mentorKey: 'leo', tags: ['work', 'planning'], dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], createdAt: Date.now() },
  { id: 'seed-2', title: 'Daily 15-minute walk', description: 'Establish a consistent walking habit for physical and mental well-being.', column: 'growing', priority: 'medium', mentorKey: 'panda', tags: ['health', 'habit'], dueDate: '', createdAt: Date.now() },
];

const getCategoryName = (mentorKey) => (
  mentorKey === 'leo' ? 'General' : MENTORS[mentorKey]?.name || 'General'
);

export default function TheGrove() {
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMentor, setFilterMentor] = useState('all');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    setTasks(saved ? JSON.parse(saved) : SAMPLE_TASKS);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  }, [tasks]);

  const filteredTasks = tasks.filter(task => {
    if (filterMentor !== 'all' && task.mentorKey !== filterMentor) return false;
    if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const tasksByColumn = COLUMNS.reduce((acc, col) => {
    acc[col.id] = filteredTasks.filter(task => task.column === col.id);
    return acc;
  }, {});

  const handleSaveTask = (taskData) => {
    if (editingTask) {
      setTasks(prev => prev.map(t => (t.id === editingTask.id ? { ...t, ...taskData } : t)));
    } else {
      const newTask = {
        id: `seed_${Date.now()}`,
        column: 'planted',
        createdAt: Date.now(),
        ...taskData
      };
      setTasks(prev => [...prev, newTask]);
    }
    setEditingTask(null);
    setShowTaskModal(false);
  };

  const moveTask = (taskId, newColumn) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, column: newColumn } : t));
  };

  const deleteTask = (id) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    setShowTaskModal(false);
  };

  return (
    <div className="flex flex-col h-full w-full space-y-6">
      {/* Hero Header */}
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl flex-shrink-0">
        <div className="absolute inset-0 bg-black/30 z-10" />
        <img 
          src={HERO_IMAGE} 
          alt="The Grove" 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            The Grove
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Where your goals take root. Organize your projects and tasks in a flourishing ecosystem.
          </p>
        </div>
      </div>

      {/* Controls Bar */}
      <Card className="p-4 flex-shrink-0">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="relative w-full lg:w-96">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
            <input 
              type="text" 
              placeholder="Search seeds..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-2.5 border border-sage/20 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sage/50" 
            />
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-charcoal/60 mr-2 flex-shrink-0" />
            <button 
              onClick={() => setFilterMentor('all')} 
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filterMentor === 'all' ? 'bg-sage text-white shadow-md' : 'bg-gray-100 text-charcoal/70 hover:bg-gray-200'}`}
            >
              All
            </button>
            {Object.values(MENTORS).map(mentor => (
              <button 
                key={mentor.key} 
                onClick={() => setFilterMentor(mentor.key)} 
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${filterMentor === mentor.key ? 'bg-sage text-white shadow-md' : 'bg-gray-100 text-charcoal/70 hover:bg-gray-200'}`}
              >
                {getCategoryName(mentor.key)}
              </button>
            ))}
          </div>

          <Button variant="primary" onClick={() => { setEditingTask(null); setShowTaskModal(true); }} icon={FiPlus} className="w-full lg:w-auto shadow-lg">
            Plant Seed
          </Button>
        </div>
      </Card>

      {/* Kanban Board */}
      <div className="flex-1 min-h-0 overflow-x-auto pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 min-w-[1000px] lg:min-w-0 h-full">
          {COLUMNS.map(column => (
            <div 
              key={column.id}
              onDragOver={(e) => { e.preventDefault(); setDragOverColumn(column.id); }}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => {
                if (draggedTask && draggedTask.column !== column.id) {
                  moveTask(draggedTask.id, column.id);
                }
                setDraggedTask(null);
                setDragOverColumn(null);
              }}
              className={`
                bg-white/60 backdrop-blur-sm rounded-2xl p-4 flex flex-col h-full border-2 transition-all
                ${dragOverColumn === column.id ? 'border-sage bg-sage/10' : 'border-transparent'}
              `}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: column.color }} />
                  <h3 className="font-cormorant font-bold text-lg text-charcoal">{column.title}</h3>
                </div>
                <span className="bg-white px-2 py-0.5 rounded-full text-xs font-bold text-charcoal/60 shadow-sm">
                  {tasksByColumn[column.id].length}
                </span>
              </div>
              
              <div className="flex-1 space-y-3 overflow-y-auto pr-2 custom-scrollbar">
                <AnimatePresence>
                  {tasksByColumn[column.id].map(task => (
                    <TaskCard 
                      key={task.id} 
                      task={task} 
                      onEdit={() => { setEditingTask(task); setShowTaskModal(true); }}
                      onDragStart={() => setDraggedTask(task)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TaskModal 
        isOpen={showTaskModal} 
        task={editingTask} 
        onSave={handleSaveTask} 
        onDelete={deleteTask} 
        onClose={() => setShowTaskModal(false)} 
      />
    </div>
  );
}

function TaskCard({ task, onEdit, onDragStart }) {
  const mentor = MENTORS[task.mentorKey];
  const priority = PRIORITY_LEVELS[task.priority];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
      draggable
      onDragStart={onDragStart}
      onClick={onEdit}
      className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 cursor-grab active:cursor-grabbing group relative"
    >
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <SafeIcon icon={FiEdit} className="w-4 h-4 text-charcoal/40" />
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide text-white" style={{ backgroundColor: priority.color }}>
          {priority.label}
        </span>
        {mentor && (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-charcoal/60 flex items-center gap-1">
            {getCategoryName(task.mentorKey)}
          </span>
        )}
      </div>

      <h4 className="font-dm-sans font-bold text-charcoal mb-1 leading-snug pr-4">{task.title}</h4>
      
      {task.dueDate && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-charcoal/50">
          <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
          <span>{new Date(task.dueDate).toLocaleDateString()}</span>
        </div>
      )}
    </motion.div>
  );
}

function TaskModal({ isOpen, task, onSave, onDelete, onClose }) {
  const [formData, setFormData] = useState({
    title: '', description: '', priority: 'medium', mentorKey: 'butler', dueDate: '', column: 'planted'
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        mentorKey: task.mentorKey || 'butler',
        dueDate: task.dueDate || '',
        column: task.column || 'planted',
      });
    } else {
      setFormData({ title: '', description: '', priority: 'medium', mentorKey: 'butler', dueDate: '', column: 'planted' });
    }
  }, [task, isOpen]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={task ? 'Edit Seed' : 'Plant New Seed'}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-bold text-charcoal mb-1">Title</label>
            <input 
              type="text" 
              value={formData.title} 
              onChange={e => setFormData({ ...formData, title: e.target.value })} 
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sage/50" 
              placeholder="What do you want to accomplish?"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-bold text-charcoal mb-1">Description</label>
            <textarea 
              value={formData.description} 
              onChange={e => setFormData({ ...formData, description: e.target.value })} 
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sage/50 min-h-[100px]" 
              placeholder="Add details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-charcoal mb-1">Priority</label>
              <select 
                value={formData.priority} 
                onChange={e => setFormData({ ...formData, priority: e.target.value })} 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl"
              >
                {Object.entries(PRIORITY_LEVELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-charcoal mb-1">Category</label>
              <select 
                value={formData.mentorKey} 
                onChange={e => setFormData({ ...formData, mentorKey: e.target.value })} 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl"
              >
                {Object.values(MENTORS).map(mentor => (
                  <option key={mentor.key} value={mentor.key}>{getCategoryName(mentor.key)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-charcoal mb-1">Status</label>
              <select 
                value={formData.column} 
                onChange={e => setFormData({ ...formData, column: e.target.value })} 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl"
              >
                {COLUMNS.map(col => (
                  <option key={col.id} value={col.id}>{col.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-charcoal mb-1">Due Date</label>
              <input 
                type="date" 
                value={formData.dueDate} 
                onChange={e => setFormData({ ...formData, dueDate: e.target.value })} 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-between bg-gray-50 rounded-b-2xl">
          {task ? (
            <Button type="button" variant="danger" onClick={() => onDelete(task.id)} icon={FiTrash2}>Delete</Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={!formData.title.trim()}>Save Seed</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}